/* One owner for touch policy, viewport sizing and overlay scroll locking. */
(() => {
  'use strict';
  const key = Symbol.for('pageone.usability');
  if (window[key]) return;
  window[key] = true;
  const root = document.documentElement;
  const body = document.body;
  let locked = false;
  let savedScroll = 0;
  let lockIntent = null;
  let quoteReturnScroll = 0;
  let focusScrollFrame = 0;
  const header = document.querySelector('[data-pageone-header]');
  const headerSpacer = document.createElement('div');
  headerSpacer.className = 'po-header-lock-spacer';
  headerSpacer.hidden = true;
  headerSpacer.setAttribute('aria-hidden', 'true');
  header?.before(headerSpacer);
  // Safari may ignore viewport scale limits; keep the landing-page gesture policy explicit.
  if (body.hasAttribute('data-pageone-landing')) {
    const preventPinch = event => {
      if (event.type.startsWith('gesture') || event.touches?.length > 1) event.preventDefault();
    };
    document.addEventListener('touchmove', preventPinch, { passive: false });
    document.addEventListener('gesturestart', preventPinch, { passive: false });
    document.addEventListener('gesturechange', preventPinch, { passive: false });
  }
  // Capture before opening an overlay changes the root's scroll container/height.
  function captureLockIntent(event) {
    if (locked) return;
    const now = performance.now();
    const control = event.target.closest?.('button, a');
    if (event.type === 'click' && lockIntent?.pointer && lockIntent.control === control && now - lockIntent.time < 1500) return;
    lockIntent = { width: body.getBoundingClientRect().width, scroll: window.scrollY, headerTop: header?.getBoundingClientRect().top, time: now, pointer: event.type === 'pointerdown', control };
  }
  document.addEventListener('pointerdown', captureLockIntent, true);
  document.addEventListener('click', captureLockIntent, true);

  function syncViewport() {
    const viewport = window.visualViewport;
    // Ignore browser-level magnification when measuring keyboard space.
    if (viewport && Math.abs(viewport.scale - 1) > .01) return;
    root.style.setProperty('--po-visible-height', Math.round(viewport?.height || innerHeight) + 'px');
    cancelAnimationFrame(focusScrollFrame);
    focusScrollFrame = requestAnimationFrame(keepFocusedFieldVisible);
  }

  function keepFocusedFieldVisible() {
    if (!matchMedia('(max-width: 760px)').matches) return;
    const field = document.activeElement;
    if (!field?.matches('input:not([type="checkbox"]), textarea, select')) return;
    const viewport = window.visualViewport;
    if (viewport && Math.abs(viewport.scale - 1) > .01) return;
    const top = viewport?.offsetTop || 0;
    const bottom = top + (viewport?.height || innerHeight);
    const bounds = field.getBoundingClientRect();
    if (bounds.top < top + 20 || bounds.bottom > bottom - 20) {
      field.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }

  document.addEventListener('focusin', () => {
    cancelAnimationFrame(focusScrollFrame);
    focusScrollFrame = requestAnimationFrame(keepFocusedFieldVisible);
  });

  // Mobile quote requests continue in the normal page, free of modal/keyboard nesting.
  document.addEventListener('pageone:open-inline-quote', () => {
    const quote = document.querySelector('#quote');
    const form = document.querySelector('#PageOneQuote');
    if (!quote || !form) return;
    quoteReturnScroll = locked ? savedScroll : window.scrollY;
    requestAnimationFrame(() => {
      syncOverlays();
      const heading = quote.querySelector('.quote-heading');
      const title = heading?.querySelector('h2');
      if (!heading || !title) return;
      let back = heading.querySelector('.mobile-quote-back');
      if (!back) {
        back = document.createElement('button');
        back.type = 'button';
        back.className = 'mobile-quote-back';
        back.textContent = 'Back to collection';
        back.addEventListener('click', () => {
          const collectionTitle = document.querySelector('#collection h2');
          collectionTitle?.setAttribute('tabindex', '-1');
          collectionTitle?.focus({ preventScroll: true });
          window.scrollTo({ top: quoteReturnScroll, behavior: 'instant' });
        });
        heading.prepend(back);
      }
      let summary = heading.querySelector('.mobile-quote-selection');
      if (!summary) {
        summary = document.createElement('p');
        summary.className = 'mobile-quote-selection';
        summary.setAttribute('role', 'status');
        heading.append(summary);
      }
      window.PageOneEnquiry?.refreshSummary();
      title.setAttribute('tabindex', '-1');
      title.focus({ preventScroll: true });
      quote.scrollIntoView({ block: 'start', behavior: 'instant' });
    });
  });

  function syncOverlays() {
    const shouldLock = body.classList.contains('po-overlay-open') || !!document.querySelector('dialog[open]');
    if (shouldLock === locked) return;
    locked = shouldLock;
    if (locked) {
      const intent = lockIntent && performance.now() - lockIntent.time < 1500 ? lockIntent : null;
      savedScroll = Math.max(0, intent?.scroll ?? window.scrollY);
      root.style.setProperty('--po-lock-width', (intent?.width ?? body.getBoundingClientRect().width) + 'px');
      if (header) {
        const bounds = header.getBoundingClientRect();
        const styles = getComputedStyle(header);
        // openPanel measured this before overflow:hidden changed sticky's scroll container.
        const panelBottom = parseFloat(root.style.getPropertyValue('--po-panel-top'));
        const viewportTop = Math.max(parseFloat(styles.top) || 0, intent?.headerTop ?? (
          body.classList.contains('po-overlay-open') && Number.isFinite(panelBottom)
            ? panelBottom - bounds.height : bounds.top
        ));
        root.style.setProperty('--po-lock-header-top', viewportTop + 'px');
        root.style.setProperty('--po-lock-header-height', bounds.height + 'px');
        root.style.setProperty('--po-lock-header-left', bounds.left + 'px');
        root.style.setProperty('--po-lock-header-width', bounds.width + 'px');
        root.style.setProperty('--po-lock-header-margin-top', styles.marginTop);
        root.style.setProperty('--po-lock-header-margin-bottom', styles.marginBottom);
        headerSpacer.hidden = false;
      }
      root.style.setProperty('--po-lock-top', -savedScroll + 'px');
      body.classList.add('po-scroll-locked');
      if (header) root.style.setProperty('--po-panel-top', header.getBoundingClientRect().bottom + 'px');
    } else {
      body.classList.remove('po-scroll-locked');
      headerSpacer.hidden = true;
      root.style.removeProperty('--po-lock-top');
      root.style.removeProperty('--po-lock-width');
      window.scrollTo({ top: savedScroll, left: 0, behavior: 'instant' });
      document.dispatchEvent(new CustomEvent('pageone:scroll-restored', { detail: { scrollY: savedScroll } }));
    }
    lockIntent = null;
  }

  // Run before the next paint, keeping the sticky-to-fixed handoff imperceptible.
  const overlayObserver = new MutationObserver(syncOverlays);
  overlayObserver.observe(body, { attributes: true, attributeFilter: ['class'] });
  document.querySelectorAll('dialog').forEach(dialog => overlayObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] }));
  window.visualViewport?.addEventListener('resize', syncViewport, { passive: true });
  window.addEventListener('resize', syncViewport, { passive: true });
  window.addEventListener('pageshow', () => { syncViewport(); syncOverlays(); });
  syncViewport();
  syncOverlays();
})();
